/* Admin Bulk Operations: list recent mass actions + undo within 7 days. */
import { useState, useEffect } from 'react';
import api from '../lib/api';
import { useLanguage } from '../contexts/LanguageContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { ArrowsClockwise, ArrowUUpLeft, Warning, CheckCircle, Clock, FilePdf, FunnelSimple } from '@phosphor-icons/react';
import { toast } from 'sonner';

const fmt = (iso) => {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleString('fr-FR'); } catch { return iso; }
};

const opTypeBadge = (type, language) => {
  const map = {
    bulk_enable: { color: 'bg-amber-50 text-amber-700 border-amber-200', label: language === 'fr' ? 'Activation masse' : 'Bulk enable' },
    csv_import: { color: 'bg-indigo-50 text-indigo-700 border-indigo-200', label: language === 'fr' ? 'Import CSV' : 'CSV import' },
    toggle_service: { color: 'bg-emerald-50 text-emerald-700 border-emerald-200', label: language === 'fr' ? 'Toggle individuel' : 'Single toggle' },
    delete_payment: { color: 'bg-red-50 text-red-700 border-red-200', label: language === 'fr' ? 'Suppr. paiement' : 'Delete payment' },
  };
  return map[type] || { color: 'bg-gray-50 text-gray-700 border-gray-200', label: type };
};

const AdminBulkOps = () => {
  const { language } = useLanguage();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [retentionDays, setRetentionDays] = useState(7);
  const [undoTarget, setUndoTarget] = useState(null);
  const [confirmText, setConfirmText] = useState('');
  const [undoing, setUndoing] = useState(false);

  // Export PDF filters
  const [filters, setFilters] = useState({
    date_from: '', date_to: '', operation_type: '__all__', status: '__all__',
  });
  const [exporting, setExporting] = useState(false);

  const handleExportPdf = async () => {
    setExporting(true);
    try {
      const params = {};
      if (filters.date_from) params.date_from = filters.date_from;
      if (filters.date_to) params.date_to = filters.date_to;
      if (filters.operation_type && filters.operation_type !== '__all__') params.operation_type = filters.operation_type;
      if (filters.status && filters.status !== '__all__') params.status = filters.status;
      const response = await api.get('/admin/bulk-operations/export-pdf', {
        params, responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `journal_operations_masse_${new Date().toISOString().slice(0, 10)}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      toast.success(language === 'fr' ? 'Export PDF généré' : 'PDF export ready');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur');
    } finally { setExporting(false); }
  };

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/admin/bulk-operations?limit=100');
      setItems(data.items || []);
      setRetentionDays(data.retention_days || 7);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur');
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const confirmUndo = async () => {
    if (!undoTarget) return;
    if (confirmText !== 'ANNULER') {
      toast.error(language === 'fr' ? 'Tapez ANNULER pour confirmer' : 'Type ANNULER to confirm');
      return;
    }
    setUndoing(true);
    try {
      const { data } = await api.post(`/admin/bulk-operations/${undoTarget.operation_id}/undo`, { confirmation: 'ANNULER' });
      toast.success(
        language === 'fr'
          ? `Opération annulée : ${data.restored} restauré(s), ${data.deleted} supprimé(s)`
          : `Operation undone: ${data.restored} restored, ${data.deleted} deleted`
      );
      setUndoTarget(null);
      setConfirmText('');
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur');
    } finally { setUndoing(false); }
  };

  return (
    <div className="space-y-6" data-testid="admin-bulk-ops-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-outfit text-3xl font-bold text-[#0F172A]">
            {language === 'fr' ? 'Historique opérations en masse' : 'Bulk operations history'}
          </h1>
          <p className="font-manrope text-sm text-[#64748B] mt-1">
            {language === 'fr'
              ? `Annulez (undo) les imports CSV, activations en masse et suppressions de paiement dans les ${retentionDays} derniers jours.`
              : `Undo CSV imports, bulk enables and payment deletes within the last ${retentionDays} days.`}
          </p>
        </div>
        <Button onClick={load} variant="outline" data-testid="refresh-ops-btn">
          <ArrowsClockwise size={16} className="mr-1" />
          {language === 'fr' ? 'Rafraîchir' : 'Refresh'}
        </Button>
      </div>

      {/* Export PDF + filters panel */}
      <Card className="border border-[#E2E8F0] shadow-none" data-testid="export-pdf-panel">
        <CardHeader className="pb-3">
          <CardTitle className="font-outfit text-base flex items-center gap-2">
            <FunnelSimple size={18} weight="duotone" className="text-indigo-600" />
            {language === 'fr' ? 'Export PDF — Journal pour audit' : 'PDF export — Audit journal'}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-5 gap-3 items-end">
            <div className="space-y-1">
              <Label className="text-xs font-manrope">{language === 'fr' ? 'Du' : 'From'}</Label>
              <Input
                type="date"
                value={filters.date_from}
                onChange={(e) => setFilters({ ...filters, date_from: e.target.value })}
                data-testid="export-date-from"
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs font-manrope">{language === 'fr' ? 'Au' : 'To'}</Label>
              <Input
                type="date"
                value={filters.date_to}
                onChange={(e) => setFilters({ ...filters, date_to: e.target.value })}
                data-testid="export-date-to"
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs font-manrope">{language === 'fr' ? 'Type' : 'Type'}</Label>
              <Select value={filters.operation_type} onValueChange={(v) => setFilters({ ...filters, operation_type: v })}>
                <SelectTrigger data-testid="export-type">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__all__">{language === 'fr' ? 'Tous' : 'All'}</SelectItem>
                  <SelectItem value="bulk_enable">{language === 'fr' ? 'Activation masse' : 'Bulk enable'}</SelectItem>
                  <SelectItem value="csv_import">{language === 'fr' ? 'Import CSV' : 'CSV import'}</SelectItem>
                  <SelectItem value="toggle_service">{language === 'fr' ? 'Toggle individuel' : 'Single toggle'}</SelectItem>
                  <SelectItem value="delete_payment">{language === 'fr' ? 'Suppr. paiement' : 'Delete payment'}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label className="text-xs font-manrope">{language === 'fr' ? 'Statut' : 'Status'}</Label>
              <Select value={filters.status} onValueChange={(v) => setFilters({ ...filters, status: v })}>
                <SelectTrigger data-testid="export-status">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__all__">{language === 'fr' ? 'Tous' : 'All'}</SelectItem>
                  <SelectItem value="active">{language === 'fr' ? 'Active' : 'Active'}</SelectItem>
                  <SelectItem value="undone">{language === 'fr' ? 'Annulée' : 'Undone'}</SelectItem>
                  <SelectItem value="expired">{language === 'fr' ? 'Expirée' : 'Expired'}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Button
                onClick={handleExportPdf}
                disabled={exporting}
                className="bg-indigo-600 hover:bg-indigo-700 text-white w-full"
                data-testid="export-pdf-btn"
              >
                <FilePdf size={16} className="mr-1" />
                {exporting
                  ? (language === 'fr' ? 'Génération…' : 'Generating…')
                  : (language === 'fr' ? 'Exporter PDF' : 'Export PDF')}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card className="border border-[#E2E8F0] shadow-none">
        <CardHeader>
          <CardTitle className="font-outfit text-base">
            {language === 'fr' ? 'Opérations récentes' : 'Recent operations'}
            <span className="text-xs font-manrope text-[#64748B] ml-2">({items.length})</span>
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-6 text-center text-[#94A3B8] font-manrope text-sm">
              {language === 'fr' ? 'Chargement…' : 'Loading…'}
            </div>
          ) : items.length === 0 ? (
            <div className="p-6 text-center text-[#94A3B8] font-manrope text-sm">
              {language === 'fr' ? 'Aucune opération en masse récente.' : 'No recent bulk operations.'}
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="border-[#E2E8F0]">
                  <TableHead>{language === 'fr' ? 'Type' : 'Type'}</TableHead>
                  <TableHead>{language === 'fr' ? 'Description' : 'Description'}</TableHead>
                  <TableHead>{language === 'fr' ? 'Par' : 'By'}</TableHead>
                  <TableHead>{language === 'fr' ? 'Quand' : 'When'}</TableHead>
                  <TableHead>{language === 'fr' ? 'Taille' : 'Size'}</TableHead>
                  <TableHead>{language === 'fr' ? 'Statut' : 'Status'}</TableHead>
                  <TableHead></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.map((op, i) => {
                  const badge = opTypeBadge(op.operation_type, language);
                  const isExpired = op.expires_at && new Date(op.expires_at) < new Date();
                  return (
                    <TableRow key={op.operation_id} className="border-[#E2E8F0]" data-testid={`op-row-${i}`}>
                      <TableCell>
                        <span className={`inline-flex text-xs font-manrope px-2 py-1 rounded-full border ${badge.color}`}>
                          {badge.label}
                        </span>
                      </TableCell>
                      <TableCell className="font-manrope text-xs max-w-md">
                        <div className="text-[#0F172A]">{op.description}</div>
                        {op.extra?.class_name && (
                          <div className="text-[#64748B] mt-0.5">Classe: {op.extra.class_name}</div>
                        )}
                      </TableCell>
                      <TableCell className="font-mono text-xs text-[#64748B]">
                        {op.performed_by?.email || '—'}
                      </TableCell>
                      <TableCell className="font-mono text-xs">{fmt(op.performed_at)}</TableCell>
                      <TableCell className="text-xs text-center">{op.snapshot_size}</TableCell>
                      <TableCell>
                        {op.undone ? (
                          <span className="inline-flex items-center gap-1 text-xs text-[#64748B]">
                            <CheckCircle size={14} />
                            {language === 'fr' ? 'Annulée' : 'Undone'}
                          </span>
                        ) : isExpired ? (
                          <span className="inline-flex items-center gap-1 text-xs text-amber-600">
                            <Clock size={14} />
                            {language === 'fr' ? 'Expirée' : 'Expired'}
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 text-xs text-emerald-600">
                            <CheckCircle size={14} />
                            {language === 'fr' ? 'Active' : 'Active'}
                          </span>
                        )}
                      </TableCell>
                      <TableCell>
                        {!op.undone && !isExpired && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => { setUndoTarget(op); setConfirmText(''); }}
                            className="border-red-300 text-red-700 hover:bg-red-50"
                            data-testid={`undo-op-btn-${i}`}
                          >
                            <ArrowUUpLeft size={14} className="mr-1" />
                            {language === 'fr' ? 'Annuler' : 'Undo'}
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Undo confirmation dialog with ANNULER guard */}
      <Dialog open={!!undoTarget} onOpenChange={(o) => { if (!o) { setUndoTarget(null); setConfirmText(''); } }}>
        <DialogContent className="max-w-md" data-testid="undo-dialog">
          <DialogHeader>
            <DialogTitle className="font-outfit text-xl text-red-700 flex items-center gap-2">
              <Warning size={22} />
              {language === 'fr' ? 'Annuler cette opération ?' : 'Undo this operation?'}
            </DialogTitle>
            <DialogDescription className="font-manrope text-[#475569] pt-3 space-y-2">
              {undoTarget && (
                <>
                  <div><strong>{language === 'fr' ? 'Description' : 'Description'}:</strong> {undoTarget.description}</div>
                  <div><strong>{language === 'fr' ? 'Effectué le' : 'Performed at'}:</strong> {fmt(undoTarget.performed_at)}</div>
                  <div><strong>{language === 'fr' ? 'Par' : 'By'}:</strong> {undoTarget.performed_by?.email}</div>
                  <div><strong>{language === 'fr' ? 'Documents affectés' : 'Documents affected'}:</strong> {undoTarget.snapshot_size}</div>
                  <div className="text-red-600 font-semibold pt-2">
                    {language === 'fr'
                      ? '⚠️ Pour confirmer, tapez exactement le mot ANNULER :'
                      : '⚠️ To confirm, type the word ANNULER exactly:'}
                  </div>
                  <Input
                    autoFocus
                    value={confirmText}
                    onChange={(e) => setConfirmText(e.target.value)}
                    placeholder="ANNULER"
                    className="font-mono"
                    data-testid="undo-confirm-input"
                  />
                </>
              )}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setUndoTarget(null); setConfirmText(''); }} disabled={undoing} data-testid="undo-cancel-btn">
              {language === 'fr' ? 'Fermer' : 'Close'}
            </Button>
            <Button
              onClick={confirmUndo}
              disabled={undoing || confirmText !== 'ANNULER'}
              className="bg-red-600 hover:bg-red-700 text-white"
              data-testid="undo-confirm-btn"
            >
              {undoing ? (language === 'fr' ? 'Annulation…' : 'Undoing…') : (language === 'fr' ? 'Annuler définitivement' : 'Undo')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminBulkOps;
